import React from "react";
import ProgressBar from "../ProgressBar";
import { useChaseMenu } from "../../store/use-chase-menu";
import ChaseItem from "./ChaseItem";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faUser } from "@fortawesome/free-solid-svg-icons";

interface IChaseDropDown {
  UpdateItemData: () => void;
}

const itemKeys = {
  declaredNcb: "Declared NCB",
  declaredMirror: "Declared MNCB",
  ncbFactor: "NCB found",
  cueFactor: "CUE factor",
};

const sectionLabel =
  "flex items-center gap-2 text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]";

const panelCard = "w-full bg-surface rounded-2xl border border-edge p-5 sm:p-6 smallTablet:!p-4";

const ChaseDropMenu = ({ UpdateItemData }: IChaseDropDown) => {
  const { initialValues } = useChaseMenu();
  const { chaseItems, drivers, timeline } = initialValues;
  const policyRef = initialValues?.data?.policyReferenceNumber;
  const portalLink = `myportal.co.uk/${policyRef}`;

  const CopytoClipboard = () => {
    navigator.clipboard.writeText(portalLink);
    toast.success("Copied");
  };

  return (
    <td colSpan={10} className="p-0">
      <div className="bg-overlay border-y border-edge-faint px-5 py-5 animate-fade-in smallTablet:sticky smallTablet:left-0 smallTablet:w-[calc(100vw-60px)] smallTablet:px-2 smallTablet:py-3">
        {/* ------------------------------- [ TIMELINE + PORTAL LINK ] ------------------------------ */}
        <div className={panelCard}>
          <div className="flex flex-col lg:flex-row items-start gap-6">
            <div className="w-full lg:flex-1 min-w-0">
              <div className="w-full flex items-center justify-between gap-4">
                <p className={sectionLabel}>
                  <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                  Chase Timeline
                </p>
              </div>
              <div className="pt-6 pb-12 lg:pb-2">
                <ProgressBar timeline={timeline.sort((a, b) => a.day - b.day)} />
              </div>
            </div>

            <div className="w-full lg:w-[260px] shrink-0">
              <div className="w-full h-fit rounded-xl px-4 py-3.5 bg-surface-elevated border border-edge">
                <p className={sectionLabel}>Portal Link</p>
                <div className="flex items-center justify-between gap-3 mt-2">
                  <h1
                    title={portalLink}
                    onClick={() =>
                      window.open(`https://${portalLink}`, "_blank")
                    }
                    className="text-[11px] text-blue font-bold cursor-pointer text-nowrap flex-1 overflow-hidden text-ellipsis hover:underline"
                  >
                    {portalLink}
                  </h1>
                  <button
                    type="button"
                    title="Copy portal link"
                    onClick={CopytoClipboard}
                    className="shrink-0 bg-active-background w-8 h-8 rounded-full flex justify-center items-center cursor-pointer border border-edge text-light-gray hover:text-text-primary hover:border-indigo-400/40 transition-colors duration-200"
                  >
                    <FontAwesomeIcon icon={faCopy} className="text-[13px]" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ------------------------------------ [ POLICY DATA ] ------------------------------------ */}
        {Object.keys(initialValues?.data?.apiValues).length ? (
          <div className={`${panelCard} mt-4`}>
            <p className={sectionLabel}>
              <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
              Policy Data
            </p>

            <div className="flex flex-wrap gap-3 mt-4">
              {Object.keys(initialValues?.data?.apiValues).map((item) => {
                const ncbFactor = initialValues?.data?.apiValues["ncbFactor"];
                const ncbFactorVal = ncbFactor
                  ? Number(ncbFactor[3]) === 0
                    ? Number(ncbFactor[4])
                    : Number(ncbFactor[3] + "" + ncbFactor[4])
                  : 0;

                return (
                  <div
                    key={item}
                    className="bg-surface-elevated border border-edge rounded-xl px-4 py-2.5 min-w-[160px] smallTablet:min-w-[140px] smallTablet:flex-1"
                  >
                    <p className="text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]">
                      {itemKeys[item]}
                    </p>
                    <p className="mt-1 text-[14px] font-semibold text-heading-color tabular-nums">
                      {item === "ncbFactor"
                        ? `${ncbFactorVal ? ncbFactorVal : "Unknown"} (${initialValues?.data?.apiValues[item]
                        })`
                        : initialValues?.data?.apiValues[item]}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* ------------------------------------ [ CHASE ITEMS ] ----------------------------------- */}
        {chaseItems
          ?.sort((a, b) => a?.id - b?.id)
          ?.map((item) => {
            return (
              <ChaseItem
                item={item}
                key={item.id}
                UpdateItemData={UpdateItemData}
              />
            );
          })}

        {/* --------------------------------- [ ADDITIONAL DRIVERS ] ------------------------------- */}
        {drivers
          ?.sort((a, b) => a?.id - b?.id)
          .map((item) => {
            const { chaseItem } = item;

            return (
              <React.Fragment key={item.id}>
                <div className="flex items-center gap-3 mt-6">
                  <div className="flex items-center gap-2 shrink-0 smallTablet:shrink smallTablet:flex-wrap smallTablet:min-w-0">
                    <div className="h-8 w-8 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                      <FontAwesomeIcon
                        icon={faUser}
                        className="text-[12px] text-indigo-500"
                      />
                    </div>
                    <h1 className="text-[15px] font-bold text-text-primary capitalize">
                      {item.name}
                    </h1>
                    <span className="text-[9px] font-bold uppercase tracking-[0.08em] text-light-gray border border-edge rounded-full px-2 py-[3px]">
                      Additional driver
                    </span>
                  </div>
                  <div className="flex-1 h-px bg-edge" />
                </div>

                {chaseItem
                  ?.sort((a, b) => a?.id - b?.id)
                  .map((chaseData) => {
                    return (
                      <ChaseItem
                        item={chaseData}
                        key={chaseData.id}
                        UpdateItemData={UpdateItemData}
                      />
                    );
                  })}
              </React.Fragment>
            );
          })}
      </div>
    </td>
  );
};

export default ChaseDropMenu;
